import { GAME, GAME_STATUS } from '../config.js';
import { getDifficultySettings } from './difficulty.js';
import { createBackendLeadItem } from './itemFactory.js';

/**
 * @param {import('react').MutableRefObject<object[]>} queueRef
 */
export function clearBackendLeadQueue(queueRef) {
    if (queueRef) queueRef.current = [];
}

/**
 * @param {import('react').MutableRefObject<object[]>} queueRef
 * @returns {object | null}
 */
export function dequeueBackendLeadForSpawn(
    queueRef,
    { boardSize, difficulty, stackIndex, recentPositions },
) {
    if (!queueRef?.current?.length) return null;

    const settings = getDifficultySettings(difficulty);
    const lifetimeMs = settings.itemLifetimeMs * GAME.BACKEND_LEAD_LIFETIME_SCALE;
    const nowMs = Date.now();

    while (queueRef.current.length > 0) {
        const reviewRow = queueRef.current.shift();
        if (!reviewRow || reviewRow.status === 'failed') continue;

        const item = createBackendLeadItem(reviewRow, {
            boardSize,
            lifetimeMs,
            nowMs,
            recentPositions,
            stackIndex,
        });

        if (item) return item;
    }

    return null;
}

/**
 * Place leads on the board when there is room; otherwise hold them for the next spawn slot.
 * @returns {{ state: object, consumed: number }}
 */
export function ingestBackendLeads(current, reviewRows, { difficulty, boardSize, queueRef }) {
    if (!reviewRows?.length) {
        return { state: current, consumed: 0 };
    }

    const isRunning = current.status === GAME_STATUS.RUNNING;
    let items = current.items;
    let consumed = 0;
    let changed = false;

    for (const reviewRow of reviewRows) {
        if (!reviewRow || reviewRow.status === 'failed') {
            consumed += 1;
            continue;
        }

        if (!isRunning) {
            queueRef.current.push(reviewRow);
            consumed += 1;
            continue;
        }

        if (items.length < GAME.MAX_ITEMS_ON_SCREEN) {
            const settings = getDifficultySettings(difficulty);
            const item = createBackendLeadItem(reviewRow, {
                boardSize,
                lifetimeMs: settings.itemLifetimeMs * GAME.BACKEND_LEAD_LIFETIME_SCALE,
                nowMs: Date.now(),
                recentPositions: items.slice(-5).map((entry) => ({ x: entry.x, y: entry.y })),
                stackIndex: items.length,
            });

            if (item) {
                items = [...items, item];
                changed = true;
                consumed += 1;
                continue;
            }
        }

        queueRef.current.push(reviewRow);
        consumed += 1;
    }

    if (!changed) {
        return { state: current, consumed };
    }

    return { state: { ...current, items }, consumed };
}
